import { useTranslation } from 'react-i18next';

import { Font, View } from '@react-pdf/renderer';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { DevelopmentTimelineTable } from 'PDF/components/DevelopmentTimeline/DevelopmentTimelineTable';
import { GoalsTable } from 'PDF/components/GoalsTable/GoalsTable';
import { IGoalsTableData } from 'PDF/components/GoalsTable/types';
import { IGeneralInfoData } from 'PDF/components/InfoTableWithIcons/InfoTableRow';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { SWOT } from 'PDF/components/SWOT/SWOT';
import { IEvent } from 'PDF/components/Timeline/VerticalTimeline';
import { VerticalTimelineImagePdf } from 'PDF/components/Timeline/VerticalTimelineImagePdf';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { hasDataToRender } from 'PDF/helpers/hasDataToRender';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import { GeneralCompanyInformationTable } from 'PDF/pages/AboutTheCompany/GeneralCompanyInformationTable/GeneralCompanyInformationTable';
import { MissionAndVisionTable } from 'PDF/pages/AboutTheCompany/MissionAndVisionTable/MissionAndVisionTable';
import { IMissionData } from 'PDF/pages/AboutTheCompany/types';
import { SectionCoverPagePdf } from 'PDF/pages/SectionCoverPage';
import { AboutCompanyKeys, AboutCompanySectionAvailability, getAboutCompanyChapterNames } from 'PDF/types/pdfSections';
import { ITimelineItem } from 'PDF/types/timeline';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { getIsGoalsAndObjectivesTableEmpty } from 'helpers/tableHelpers';

import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

export interface IAboutCompanyPdfProps {
  magazineItems: ITextItem[][];
  generalInfoTableData: IGeneralInfoData[];
  missionAndVisionTableData: IMissionData[];
  goalsAndObjectivesTableData: IGoalsTableData;
  swotTableData: { [p: string]: any };
  historicalEventsTimelineImageSrc: string;
  historicalEventsTimelineData: IEvent[];
  businessPlanType: IBusinessToolType;
  stages: ITimelineItem[];
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const AboutTheCompanyPdf = (
  props: IAboutCompanyPdfProps & {
    availability: AboutCompanySectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    generalInfoTableData,
    missionAndVisionTableData,
    goalsAndObjectivesTableData,
    swotTableData,
    historicalEventsTimelineImageSrc,
    historicalEventsTimelineData,
    businessPlanType,
    stages,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage,
  } = props;

  const { t } = useTranslation();

  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.ABOUT_COMPANY, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const ABOUT_COMPANY_CHAPTER_NAMES = getAboutCompanyChapterNames(isExamplePage);
  const subChapterNames = (Object.keys(availability) as AboutCompanyKeys[])
    .filter((key) => !!availability[key])
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${ABOUT_COMPANY_CHAPTER_NAMES[key]}`);

  const isGoalsAndObjectivesEmpty = getIsGoalsAndObjectivesTableEmpty(goalsAndObjectivesTableData);

  const isSectionEmpty = Object.values(availability).every((value) => !value) && isGoalsAndObjectivesEmpty;
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.ABOUT_COMPANY)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 0, variant: 'before' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.generalInformation && (
            <>
              <SubChapterHeading
                heading={getSubHeading(ABOUT_COMPANY_CHAPTER_NAMES.generalInformation)}
                id="generalInformation"
              />
              <PdfContentText items={magazineItems[0]} />
              <GeneralCompanyInformationTable data={generalInfoTableData} />
            </>
          )}
          {availability.companyHistory && (
            <>
              <View minPresenceAhead={isInvestor ? 700 : magazineItems[1]?.some((item) => item.text) ? 0 : 700} />
              <SubChapterHeading
                heading={getSubHeading(ABOUT_COMPANY_CHAPTER_NAMES.companyHistory)}
                id="companyHistory"
              />
              {!isInvestor && <PdfContentText items={magazineItems[1]} />}
              {hasDataToRender(historicalEventsTimelineData) && (
                <VerticalTimelineImagePdf
                  imageSrc={historicalEventsTimelineImageSrc}
                  itemsNumber={historicalEventsTimelineData.length}
                  heading={
                    isExamplePage ? 'Key Historical Events' : t('pdfPages.mappings.aboutCompany.keyHistoryEvents')
                  }
                />
              )}
            </>
          )}
          {availability.aboutTheProject && (
            <>
              <SubChapterHeading
                heading={getSubHeading(ABOUT_COMPANY_CHAPTER_NAMES.aboutTheProject)}
                id="aboutTheProject"
              />
              <PdfContentText items={magazineItems[1]} withoutBottomMargin={isGoalsAndObjectivesEmpty} />
              {!isGoalsAndObjectivesEmpty && (
                <GoalsTable
                  tableData={goalsAndObjectivesTableData}
                  heading={isExamplePage ? 'Main Goals and Objectives' : t('pdfPages.mappings.aboutCompany.mainGoals')}
                />
              )}

              <PdfContentText items={magazineItems[2]} />
            </>
          )}
          {availability.missionAndStrategy && (
            <>
              {(!missionAndVisionTableData || !missionAndVisionTableData.length) && (
                <SubChapterHeading
                  heading={getSubHeading(ABOUT_COMPANY_CHAPTER_NAMES.missionAndStrategy)}
                  id="missionAndStrategy"
                />
              )}

              <MissionAndVisionTable
                tableData={missionAndVisionTableData}
                heading={getSubHeading(ABOUT_COMPANY_CHAPTER_NAMES.missionAndStrategy)}
              />
              {!isGoalsAndObjectivesEmpty && (
                <GoalsTable
                  tableData={goalsAndObjectivesTableData}
                  heading={isExamplePage ? 'Main Goals and Objectives' : t('pdfPages.mappings.aboutCompany.mainGoals')}
                />
              )}
              <PdfContentText items={magazineItems[2]} />
            </>
          )}
          {availability.stages && (
            <>
              <SubChapterHeading heading={getSubHeading(ABOUT_COMPANY_CHAPTER_NAMES.stages)} id="stages" />
              {stages.length > 0 && <DevelopmentTimelineTable items={stages} isExamplePage={isExamplePage} />}
            </>
          )}
          {availability.swotAnalysis && (
            <SWOT
              heading={getSubHeading(ABOUT_COMPANY_CHAPTER_NAMES.swotAnalysis)}
              data={swotTableData}
              isExamplePage={isExamplePage}
            />
          )}
        </PDFPage>
      )}
    </>
  );
};
